import React, { useState } from "react";

import {
	Button,
	InfoMessage,
	Modal,
	NumberInput,
	Toggle,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";

import { adminKeys, subscriptionKeys } from "@/constants/CQueryKeys";

import {
	TAdminPlan,
	TAdminPlanChanges,
	TAdminPlanImpact,
} from "@/types/TAdmin";

import { errorToast, promiseToast } from "@/modules/index";

const LIMITS = [
	["maxProjects", "projects"],
	["maxCompanies", "companies"],
	["maxClients", "clients"],
	["maxMembers", "members"],
] as const;

const FLAGS = ["canExportPdf", "canInvoice", "canShare"] as const;

type TForm = Required<TAdminPlanChanges>;

const formOf = (plan: TAdminPlan): TForm => ({
	maxProjects: plan.maxProjects,
	maxCompanies: plan.maxCompanies,
	maxClients: plan.maxClients,
	maxMembers: plan.maxMembers,
	canExportPdf: plan.canExportPdf,
	canInvoice: plan.canInvoice,
	canShare: plan.canShare,
});

/** Only what differs from the plan: the API is sent nothing else. */
export const changesOf = (plan: TAdminPlan, form: TForm): TAdminPlanChanges =>
	Object.fromEntries(
		Object.entries(form).filter(
			([key, value]) => plan[key as keyof TForm] !== value
		)
	) as TAdminPlanChanges;

const shown = (value: number | boolean | null) =>
	value === null
		? "∞"
		: typeof value === "boolean"
			? t(value ? "admin.plans.edit.on" : "admin.plans.edit.off")
			: String(value);

interface IAdminPlanEditModalProps {
	plan: TAdminPlan;
	onClose: () => void;
}

/**
 * Limits and feature flags of one plan, in two steps: the form, then what
 * the change reaches (a dry run: workspaces on the plan, those already
 * over a lowered limit) before it is applied. Every workspace on the plan
 * feels it at once; nothing is taken away, only the next creation.
 */
export const AdminPlanEditModal: React.FC<IAdminPlanEditModalProps> = ({
	plan,
	onClose,
}) => {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<TForm>(() => formOf(plan));
	const [impact, setImpact] = useState<TAdminPlanImpact | null>(null);
	const changes = changesOf(plan, form);
	const hasChanges = Object.keys(changes).length > 0;

	const preview = useMutation({
		mutationFn: () => AdminService.updatePlan(plan.id, changes, true),
		onSuccess: setImpact,
	});

	const apply = useMutation({
		mutationFn: () => AdminService.updatePlan(plan.id, changes),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: adminKeys.plans });
			// The pricing grid and every account's usage read these rows.
			void queryClient.invalidateQueries({
				queryKey: subscriptionKeys.plans,
			});
			void queryClient.invalidateQueries({
				queryKey: subscriptionKeys.current,
			});
			onClose();
		},
	});

	const set = <K extends keyof TForm>(key: K, value: TForm[K]) => {
		setForm((previous) => ({ ...previous, [key]: value }));
		setImpact(null);
	};

	const footer = impact ? (
		<>
			<Button
				variant="ghost"
				color="neutral"
				onClick={() => setImpact(null)}
			>
				{t("admin.plans.edit.back")}
			</Button>
			<Button
				color="primary"
				isLoading={apply.isPending}
				onClick={() =>
					promiseToast(apply.mutateAsync(), {
						success: t("admin.plans.edit.saved"),
					})
				}
				data-testid="plan-edit-apply"
			>
				{t("admin.plans.edit.apply")}
			</Button>
		</>
	) : (
		<>
			<Button variant="ghost" color="neutral" onClick={onClose}>
				{t("actions.cancel")}
			</Button>
			<Button
				color="primary"
				disabled={!hasChanges}
				isLoading={preview.isPending}
				onClick={() => void errorToast(preview.mutateAsync())}
				data-testid="plan-edit-preview"
			>
				{t("admin.plans.edit.preview")}
			</Button>
		</>
	);

	return (
		<Modal
			open
			onClose={onClose}
			title={t("admin.plans.edit.title", { plan: plan.name })}
			footer={footer}
			portal
		>
			{impact ? (
				<div
					className="flex flex-col gap-4"
					data-testid="plan-edit-impact"
				>
					<ul className="flex flex-col gap-1 text-sm">
						{(Object.keys(changes) as (keyof TForm)[]).map(
							(key) => (
								<li key={key}>
									<span className="font-medium">
										{t(`admin.plans.edit.fields.${key}`)}
									</span>{" "}
									<span className="text-neutral-500">
										{shown(plan[key])}
									</span>{" "}
									→ <b>{shown(form[key])}</b>
								</li>
							)
						)}
					</ul>
					<InfoMessage color="info">
						{t("admin.plans.edit.reach", {
							count: impact.workspaces,
						})}
					</InfoMessage>
					{Object.entries(impact.overLimit)
						.filter(([, count]) => (count ?? 0) > 0)
						.map(([limit, count]) => (
							<InfoMessage
								key={limit}
								color="warning"
								data-testid={`plan-edit-over-${limit}`}
							>
								{t("admin.plans.edit.over", {
									count,
									limit: t(
										`admin.plans.edit.limits.${limit}`
									),
								})}
							</InfoMessage>
						))}
				</div>
			) : (
				<div className="flex flex-col gap-4">
					<p className="text-sm text-neutral-500">
						{t("admin.plans.edit.hint")}
					</p>
					<div className="grid gap-3 sm:grid-cols-2">
						{LIMITS.map(([key]) => (
							<NumberInput
								key={key}
								label={t(`admin.plans.edit.fields.${key}`)}
								value={form[key]}
								min={0}
								onChange={(value) => set(key, value)}
								placeholder="∞"
								data-testid={`plan-edit-${key}`}
							/>
						))}
					</div>
					<div className="flex flex-col gap-2">
						{FLAGS.map((key) => (
							<Toggle
								key={key}
								label={t(`admin.plans.edit.fields.${key}`)}
								checked={form[key]}
								onChange={(checked) => set(key, checked)}
							/>
						))}
					</div>
				</div>
			)}
		</Modal>
	);
};
